import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlayCircle, Send } from 'lucide-react';

// Mock data for active models
const mockActiveModels = [
  {
    id: 'model-1',
    name: 'Finetuned-Llama3-8B (v1.2)',
  },
  {
    id: 'model-4',
    name: 'General-Purpose-Mistral-7B (v2.0)',
  }
];

const ModelPlaygroundPage: React.FC = () => {
  const [response, setResponse] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedRating, setSelectedRating] = React.useState<number | null>(null);
  const [showFeedback, setShowFeedback] = React.useState(false);

  const handleGenerate = () => {
    setIsLoading(true);
    setResponse('');
    setShowFeedback(false);
    setSelectedRating(null);
    setTimeout(() => {
      setResponse('The total revenues for Q3 2023 were $15.2 million, a 12% increase year-over-year.');
      setIsLoading(false);
      setShowFeedback(true);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Model Playground</h1>
        <p className="text-muted-foreground">Test our active models and provide feedback to help us improve.</p>
      </div>

      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Inference Test</CardTitle>
          <CardDescription>Select a model, enter a prompt, and see what it generates.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Model</Label>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select an active model" />
              </SelectTrigger>
              <SelectContent>
                {mockActiveModels.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="prompt">Your Prompt</Label>
            <Textarea id="prompt" placeholder="e.g., What were the key takeaways from the Q3 financial report?" rows={4} />
          </div>
          <Button onClick={handleGenerate} disabled={isLoading}>
            <PlayCircle className="mr-2 h-4 w-4" />
            {isLoading ? 'Generating...' : 'Generate Response'}
          </Button>
          {(isLoading || response) && (
            <div className="space-y-2">
              <Label>Model Response</Label>
              <div className="p-4 border rounded-md bg-muted min-h-[100px]">
                {isLoading ? <span className="text-muted-foreground">Thinking...</span> : response}
              </div>
            </div>
          )}
          {showFeedback && (
            <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                    <Label>Rate this response (1=Bad, 10=Excellent)</Label>
                    <div className="flex flex-wrap gap-2">
                        {Array.from({ length: 10 }, (_, i) => i + 1).map(rating => (
                            <Button 
                                key={rating} 
                                variant={selectedRating === rating ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setSelectedRating(rating)}
                            >
                                {rating}
                            </Button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="feedback-comment">Additional Comments (optional)</Label>
                    <Textarea id="feedback-comment" placeholder="What did you like or dislike?" rows={3} />
                </div>
                <Button>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Feedback
                </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModelPlaygroundPage;
